import {Component, ElementRef, Inject, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {AddJutePattern} from '@application/command/add-jute-pattern';
import {Pattern} from '@domain/pattern';
import {ManualNonProductionItemDrawing} from '@domain/production-schedule/manual-non-production-item-drawing';
import {PatternType} from '@presentation/components/select-pattern/pattern-type.enum';
import {SelectPatternComponentMatDialogConfigDataInterface} from '@presentation/components/select-pattern/select-pattern-component-mat-dialog-config.interface';
import {SelectPatternComponent} from '@presentation/components/select-pattern/select-pattern.component';
import {WeaveQualityType} from '@presentation/pages/textile-data/machine-quality/overview/weave-quality-type.enum';
import {AssertionUtils, BaseComponent, canShowErrorForErrorCodeAndFormControlForFormGroup, DialogBuilderFactoryService, FormValueHelper, TranslateService} from '@vdw/angular-component-library';
import {takeUntil} from 'rxjs/operators';

@Component({
  templateUrl: './add-jute-to-path.component.html',
  styleUrls: ['./add-jute-to-path.component.scss']
})
export class AddJuteToPathComponent extends BaseComponent implements OnInit {
  @ViewChild('matCardElement', {read: ElementRef}) public matCardElement: ElementRef;

  public addJuteToPathForm: FormGroup<{
    heightInCm: FormControl<number>;
    heightInPx: FormControl<number>;
    pattern: FormControl<Pattern>;
  }>;

  private readonly pickDensity: number;
  private readonly picksPerColorLine: number;
  private readonly weaveStructureId: number;
  private readonly qualityId: number;
  private readonly colorSetId: number;
  private readonly juteWidthInDents: number;
  private readonly weaveQualityType: WeaveQualityType;

  public readonly PATTERN_CONDITIONAL_CHIP_VALUE_KEY = 'Pattern %(id)';

  public constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly formBuilder: FormBuilder,
    private readonly dialogRef: MatDialogRef<AddJuteToPathComponent>,
    private readonly translate: TranslateService,
    private readonly addJutePattern: AddJutePattern,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService
  ) {
    super();

    this.pickDensity = data.pickDensity;
    this.picksPerColorLine = data.picksPerColorLine;
    this.weaveStructureId = data.weaveStructureId;
    this.qualityId = data.qualityId;
    this.colorSetId = data.colorSetId;
    this.juteWidthInDents = data.juteWidthInDents;
    this.weaveQualityType = data.weaveQualityType;
  }

  public ngOnInit(): void {
    this.setFormFields();
  }

  public applyChangesToProductionScheduleItemsInSchedule(): void {
    if (this.hasChosenPatternOrPatternFields) {
      this.dialogRef.close(
        new ManualNonProductionItemDrawing(
          0,
          this.addJuteToPathForm.value.pattern.name ?? this.translate.instant('PRODUCTION_ORDER.WEAVE_PLAN.JUTE.JUTE'),
          {
            widthInPx: this.juteWidthInDents,
            heightInPx: this.addJuteToPathForm.getRawValue().heightInPx
          },
          this.addJuteToPathForm.value.pattern
        )
      );
    }
  }

  public canShowErrorForFormControl(errorCode: string, formControlName: string, formGroup: FormGroup): boolean {
    return canShowErrorForErrorCodeAndFormControlForFormGroup(errorCode, formControlName, formGroup);
  }

  public getMinPatternHeight(): number {
    let pattern = this.addJuteToPathForm.value.pattern;
    return pattern?.ySizeInColorlines ?? pattern?.heightInPixels ?? 1;
  }

  public getPattern(patternFormControl: string): Pattern | string[] {
    return this.addJuteToPathForm.value[patternFormControl];
  }

  public hasChosenPatternOrPatternFields(): boolean {
    return !AssertionUtils.isNullOrUndefined(this.addJuteToPathForm.value.pattern);
  }

  public getWeaveStructureId(): number {
    return this.weaveStructureId;
  }

  public getQualityType(): WeaveQualityType {
    return this.weaveQualityType;
  }

  public canLockButton(): boolean {
    return !this.hasChosenPatternOrPatternFields();
  }

  private setFormFields(): void {
    this.addJuteToPathForm = this.formBuilder.group({
      heightInCm: this.formBuilder.control(null, [Validators.required, Validators.min(0.1)]),
      heightInPx: this.formBuilder.control(null, [Validators.required, Validators.min(1)]),
      pattern: this.formBuilder.control(null, Validators.required)
    });

    this.syncCmToPixelsFormFields();

    this.addJuteToPathForm.controls.heightInCm.disable();
    this.addJuteToPathForm.controls.heightInPx.disable();

    this.addJuteToPathForm.controls.heightInPx.setValue(1);
  }

  public cancelEvent(): void {
    this.dialogRef.close();
  }

  private syncCmToPixelsFormFields(): void {
    FormValueHelper.syncValuesOfFormFieldsByFactor(
      this.addJuteToPathForm.controls.heightInCm,
      this.addJuteToPathForm.controls.heightInPx,
      (this.pickDensity * 10) / this.picksPerColorLine,
      this.picksPerColorLine / (this.pickDensity * 10),
      0.1
    )
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe();
  }

  public selectPattern(patternFormControl: string): void {
    this.dialogBuilderFactoryService
      .getBuilder()
      .openSelectGridDialog<SelectPatternComponentMatDialogConfigDataInterface<Pattern>, Pattern>(SelectPatternComponent, {
        patternType: PatternType.JUTE_PATTERNS_OR_DRAWINGS,
        patternTranslationKey: 'TEXTILE_DATA.MACHINE_QUALITY.PATTERN_OR_DRAWING',
        showPatternTypeFilter: true,
        allowPatternCreation: true,
        addPatternCommand: this.addJutePattern.execute({
          machineQualityId: this.qualityId,
          colorSetId: this.colorSetId
        }),
        weaveStructureId: this.weaveStructureId,
        virtualScrolling: true
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((pattern: Pattern | undefined) => {
        if (pattern !== undefined) {
          const isCurrentPatternEqualToSelectedPattern = this.addJuteToPathForm.value[patternFormControl] === pattern;
          this.addJuteToPathForm.controls[patternFormControl].setValue(pattern, {emitEvent: !isCurrentPatternEqualToSelectedPattern});
          this.addJuteToPathForm.controls.heightInCm.enable();
          this.addJuteToPathForm.controls.heightInPx.enable();
          this.addJuteToPathForm.controls.heightInPx.setValidators([Validators.required, Validators.min(pattern.ySizeInColorlines ?? pattern.heightInPixels)]);
          this.addJuteToPathForm.controls.heightInPx.updateValueAndValidity();
          this.addJuteToPathForm.controls.heightInPx.setValue(pattern.ySizeInColorlines ?? pattern.heightInPixels);
          if (!isCurrentPatternEqualToSelectedPattern) {
            this.addJuteToPathForm.markAsDirty();
          }
        }
      });
  }
}
